import icEditBlue from "@/assets/icons/ic-edit-blue.svg";
import icTrashRed from "@/assets/icons/ic-trash-red.svg";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { Input } from "@/components/ui/input";
import Pagination from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { formatDate, STATUS_CONFIG_ADMIN } from "@/lib/utils";
import { AdminAgent, deleteAgent, getAgentList } from "@/services/manage-agent";
import IconPlus from "@/assets/icons/ic-plus.svg";
import { SearchInput } from "@/components/common/search-input";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IconHelp from "@/assets/icons/icon-help.svg";
import { usePermissions } from "@/lib/permissions";
import IcNodata from "@/assets/icons/ic-nodata.svg";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "under_review", label: "검토 중" },
  { value: "rejected", label: "거부됨" },
  { value: "completed", label: "완료" },
  { value: "deleted", label: "삭제됨" },
];

const PAGE_SIZE = 10;

const ManageAgentListPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can.create("agent_management");
  const canUpdate = can.update("agent_management");
  const canDelete = can.delete("agent_management");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [agents, setAgents] = useState<AdminAgent[]>([]);
  const [total, setTotal] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAgents = useCallback(async () => {
    if (hasFetchedRef.current) {
      setIsSearching(true);
    }
    try {
      const params: Record<string, string | number> = {
        page: currentPage,
        page_size: PAGE_SIZE,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      const res = await getAgentList(params);
      if (res.success && res.data) {
        setAgents(res.data.items);
        setTotal(res.data.total);
      }
    } catch {
      setAgents([]);
      setTotal(0);
    } finally {
      setIsInitialLoading(false);
      setIsSearching(false);
      hasFetchedRef.current = true;
    }
  }, [currentPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    setDeleteTargetId(agentId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteAgent(deleteTargetId);
      toast({ description: "에이전트가 삭제되었습니다." });
      fetchAgents();
    } catch {
      toast({
        description: "에이전트 삭제에 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleEdit = (e: React.MouseEvent, agentId: string) => {
    e.stopPropagation();
    navigate(`/agents/${agentId}/edit`);
  };

  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="bg-white flex flex-col gap-6"
        style={{ display: isInitialLoading ? "none" : "flex" }}
      >
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Search */}
          <SearchInput
            placeholder="이름, 공급사 또는 적용 분야으로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setDebouncedSearch(searchQuery.trim());
                setCurrentPage(1);
              }
            }}
            onClear={handleClearSearch}
          />

          {/* <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[127px]">
              <SelectValue placeholder="전체 상태" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select> */}
        </div>

        {/* Add Agent Button */}
        {canCreate && (
          <Button
            onClick={() => navigate("/agents/add-agent")}
            className="h-12 px-5 rounded font-bold text-base"
          >
            <img src={IconPlus} alt="plus" className="w-5 h-5" />새 에이전트
          </Button>
        )}
      </div>
      {/* Record Count */}
      <div className="flex">
        <span className="text-sm text-[#B0B0B0]">
          전체 {total}개 에이전트 중 {agents.length}개 표시 중
        </span>
      </div>
      {/* Table */}
      <div
        className={`border border-[#00000017] rounded overflow-x-auto min-w-0 w-full transition-opacity duration-200 ${isSearching ? "pointer-events-none" : ""}`}
      >
        <Table className="min-w-[1080px]">
          <TableHeader>
            <TableRow className="bg-[#EBEBEB]">
              <TableHead className="w-[252px] max-w-[252px]">에이전트명</TableHead>
              <TableHead className="w-[168px] max-w-[168px]">공급사</TableHead>
              <TableHead className="w-[219px] max-w-[219px]">적용 분야</TableHead>
              <TableHead className="w-[126px]">상태</TableHead>
              <TableHead className="w-[135px]">등록일</TableHead>
              <TableHead className="w-[180px] text-right">관리</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agents.map((agent) => {
              const config = STATUS_CONFIG_ADMIN[agent.status] || STATUS_CONFIG_ADMIN.under_review;
              return (
                <TableRow
                  key={agent.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/agents/${agent.id}`)}
                >
                  <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[252px] text-[16px] truncate p-3">
                    <TruncatedTooltip>{agent.name}</TruncatedTooltip>
                  </TableCell>
                  <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[168px] text-[16px] truncate p-3">
                    <TruncatedTooltip>{agent.provider}</TruncatedTooltip>
                  </TableCell>

                  <TableCell className="text-[rgba(0,0,0,0.91)] max-w-[219px] text-[16px] truncate p-3">
                    <TruncatedTooltip>{agent.application_area}</TruncatedTooltip>
                  </TableCell>
                  <TableCell className="max-w-[126px]">
                    <div
                      className={`inline-flex items-center gap-1 h-6 rounded ${config.bg} ${config.text} ${config.padding}`}
                    >
                      <span className="text-sm font-medium">{config.label}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-[rgba(0,0,0,0.91)] whitespace-nowrap p-3 text-[16px] max-w-[135px] ">
                    {formatDate(agent.created_at, "YYYY-MM-DD")}
                  </TableCell>

                  <TableCell className="p-3 max-w-[188px]">
                    <div className="flex items-center justify-end gap-1">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-[80px] h-[32px]  text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                          onClick={(e) => handleDeleteClick(e, agent.id)}
                        >
                          삭제
                        </Button>
                      )}
                      {canUpdate &&
                        (agent.status === "under_review" || agent.status === "completed") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px]  text-[12px] font-bold text-[#000000] border border-[rgba(0,0,0,0.15)]"
                            onClick={(e) => handleEdit(e, agent.id)}
                          >
                            수정
                          </Button>
                        )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {agents.length === 0 && !isInitialLoading && !isSearching && (
              <TableRow>
                <TableCell colSpan={6} className="h-[600px] text-center">
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <img src={IcNodata} alt="No data" className="" />
                    <p className=" text-[32px] text-[#000000E8]">
                      {debouncedSearch ? "검색 결과가 없습니다" : "No data"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          className="justify-center"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => !open && setDeleteTargetId(null)}
        iconVariant="neutral"
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        title="에이전트 삭제 확인"
        description="해당 에이전트를 삭제하시겠습니까?"
        primaryAction={{ label: "네", onClick: handleDeleteConfirm }}
        secondaryAction={{
          label: "아니요",
          onClick: () => setDeleteTargetId(null),
        }}
      />
    </div>
    </>
  );
};

export default ManageAgentListPage;
